/**
 * Backlog Service
 * AIDLC Administrative Backend
 *
 * API calls for backlog item CRUD operations.
 */

import apiClient from './api';
import type { BacklogItem, BacklogSummary, TokenEstimate } from '../types/backlog';

const BACKLOG_PATH = '/backlog';

/**
 * Get all backlog items (with optional query params)
 */
export async function getBacklogItems(params?: Record<string, string>): Promise<BacklogItem[]> {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, v); });
  }
  const qs = query.toString();
  const response = await apiClient.get<BacklogItem[]>(`${BACKLOG_PATH}${qs ? `?${qs}` : ''}`);
  return response.data;
}

/**
 * Get backlog summary stats
 */
export async function getBacklogSummary(): Promise<BacklogSummary> {
  const response = await apiClient.get<BacklogSummary>(`${BACKLOG_PATH}/summary`);
  return response.data;
}

export interface TriageItem {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  project: string;
  assignedAgent: string | null;
  /** Who filed it: how a dispatcher's item is told from a person's. */
  reportedBy: string;
  createdAt: string;
  estimatedCost: number | null;
  estimatedTokens: number | null;
  estimateConfidence: 'low' | 'medium' | 'high' | null;
  actualCost: number;
  actualTokens: number;
  runs: number;
}

export interface TriageQueue {
  items: TriageItem[];
  totals: {
    count: number;
    estimatedCost: number;
    actualCost: number;
    alreadySpentOn: number;
    withoutEstimate: number;
  };
}

/**
 * Open work that belongs to no sprint, with the baseline estimate each was given
 * and what has already been spent on it.
 */
export async function getUnsprintedItems(): Promise<TriageQueue> {
  const response = await apiClient.get<TriageQueue>(`${BACKLOG_PATH}/unsprinted`);
  return response.data;
}

/**
 * Create a new backlog item
 */
export async function createBacklogItem(item: {
  title: string;
  description: string;
  type: string;
  priority: string;
  status?: string;
  assignedAgent?: string | null;
  project?: string;
  labels?: string[];
}): Promise<BacklogItem> {
  const response = await apiClient.post<BacklogItem>(BACKLOG_PATH, item);
  return response.data;
}

/**
 * Update a backlog item
 */
export async function updateBacklogItem(id: string, updates: Partial<BacklogItem>): Promise<BacklogItem> {
  const response = await apiClient.put<BacklogItem>(`${BACKLOG_PATH}/${id}`, updates);
  return response.data;
}

/**
 * Delete a backlog item
 */
export async function deleteBacklogItem(id: string): Promise<void> {
  await apiClient.delete(`${BACKLOG_PATH}/${id}`);
}

/**
 * Add a comment to a backlog item
 */
export async function addBacklogComment(itemId: string, content: string): Promise<BacklogItem> {
  const response = await apiClient.post<BacklogItem>(`${BACKLOG_PATH}/${itemId}/comments`, { content });
  return response.data;
}

/**
 * Dispatch a backlog item to its assigned agent (Start + queue prompt)
 */
export async function dispatchBacklogItem(id: string): Promise<{ item: BacklogItem; promptId: string }> {
  const response = await apiClient.post(`${BACKLOG_PATH}/${id}/dispatch`);
  return response.data;
}

/**
 * Stop a backlog item (revert to open, cancel pending prompts)
 */
export async function stopBacklogItem(id: string): Promise<{ item: BacklogItem; cancelledPrompts: number }> {
  const response = await apiClient.post(`${BACKLOG_PATH}/${id}/stop`);
  return response.data;
}

/**
 * Bulk dispatch multiple backlog items
 */
export async function bulkDispatchBacklogItems(itemIds: string[]): Promise<{
  dispatched: number; failed: number;
  results: Array<{ itemId: string; success: boolean; promptId?: string; error?: string }>;
}> {
  const response = await apiClient.post(`${BACKLOG_PATH}/bulk-dispatch`, { itemIds });
  return response.data;
}

/**
 * Bulk stop multiple backlog items
 */
export async function bulkStopBacklogItems(itemIds: string[]): Promise<{
  stopped: number; failed: number;
  results: Array<{ itemId: string; success: boolean; cancelledPrompts?: number; error?: string }>;
}> {
  const response = await apiClient.post(`${BACKLOG_PATH}/bulk-stop`, { itemIds });
  return response.data;
}

export async function generateEstimate(itemId: string, agentId?: string, model?: string): Promise<{
  item: BacklogItem;
  estimate: TokenEstimate & { taskClassLabel: string; basedOnHistory: boolean; historicalSampleSize: number };
}> {
  const response = await apiClient.post(`${BACKLOG_PATH}/${itemId}/estimate`, {
    agentId,
    model: model || 'Opus 4.6',
  });
  return response.data;
}
